import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  PCloseStepStatus,
  PeriodCloseChecklistResponse,
  PeriodCloseStep,
  UpdatePeriodCloseStepInput,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Keyed by (companyId, period) so each month is cached independently and
// invalidating one period doesn't trash the others.
const key = (companyId: string | undefined, period: string | undefined) =>
  ['period-close', companyId, period] as const;

export function usePeriodCloseChecklist(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: key(companyId, period),
    queryFn: () =>
      api<PeriodCloseChecklistResponse>(`/companies/${companyId}/period-close/${period}`),
    enabled: !!companyId && !!period,
  });
}

interface UpdateStepArgs {
  stepNo: number;
  status?: PCloseStepStatus;
  notes?: string | null;
}

export function useUpdatePeriodCloseStep(
  companyId: string | undefined,
  period: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepNo, status, notes }: UpdateStepArgs) => {
      const body: UpdatePeriodCloseStepInput = {
        ...(status !== undefined ? { status } : {}),
        ...(notes !== undefined ? { notes } : {}),
      };
      return apiJson<UpdatePeriodCloseStepInput, PeriodCloseStep>(
        'PATCH',
        `/companies/${companyId}/period-close/${period}/steps/${stepNo}`,
        body,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId, period) }),
  });
}

// closePeriod also flips Period.status to CLOSED → invalidate the periods
// list so the period selector / JV-post pages refetch the new status.
export function useClosePeriod(companyId: string | undefined, period: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<PeriodCloseChecklistResponse>(`/companies/${companyId}/period-close/${period}/close`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(companyId, period) });
      void qc.invalidateQueries({ queryKey: ['periods', companyId] });
    },
  });
}
